import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Store, Upload, X, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import ItemsTab from './RestaurantProfile/ItemsTab';
import OffersTab from './RestaurantProfile/OffersTab';
import GalleryTab from './RestaurantProfile/GalleryTab';
import OrderTab from './RestaurantProfile/OrderTab';
import TransactionsTab from './RestaurantProfile/TransactionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const money = (v) =>
  `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const StatCard = ({ label, value, info, lines = [] }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardDescription className="flex items-center gap-1.5">
        {label}
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label={`What is ${label}?`} className="text-gray-400 hover:text-gray-700">
              <Info className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs leading-snug">{info}</TooltipContent>
        </Tooltip>
      </CardDescription>
      <CardTitle className="text-3xl">{value}</CardTitle>
    </CardHeader>
    {lines.length > 0 && (
      <CardContent className="pt-0 space-y-1">
        {lines.map(([k, v, cls]) => (
          <div key={k} className="flex items-center justify-between text-xs text-gray-500">
            <span>{k}</span>
            <span className={`font-semibold ${cls || 'text-gray-800'}`}>{v}</span>
          </div>
        ))}
      </CardContent>
    )}
  </Card>
);

const RestaurantProfile = () => {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRestaurant();
  }, [id]);

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API}/restaurant-profile/${id}/profile`);
      setRestaurant(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
      toast.error('Failed to load restaurant');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p>Restaurant not found</p>
      </div>
    );
  }

  const stats = restaurant.stats || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div className="flex flex-wrap items-center gap-4">
          {restaurant.frontImage && (
            <img
              src={restaurant.frontImage}
              alt={restaurant.name}
              className="w-20 h-20 rounded-lg object-cover"
            />
          )}
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
              {restaurant.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <Badge className={restaurant.status ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                {restaurant.status ? 'Active' : 'Inactive'}
              </Badge>
              <Badge className={restaurant.isOpen ? 'bg-blue-100 text-blue-800 border-0' : 'bg-gray-100 text-gray-800 border-0'}>
                {restaurant.isOpen ? 'Open Now' : 'Closed'}
              </Badge>
              <span className="text-gray-600">{restaurant.address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards — every number has a tooltip explaining exactly what it counts */}
      <TooltipProvider delayDuration={100}>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
          <StatCard
            label="Orders"
            value={stats.orders?.total ?? stats.totalOrders ?? 0}
            info="All orders ever placed at this restaurant (any status, excluding deleted ones)."
            lines={[
              ['Delivered', stats.orders?.completed ?? 0, 'text-green-700'],
              ['In progress', stats.orders?.inProgress ?? 0, 'text-blue-700'],
              ['Cancelled', stats.orders?.cancelled ?? 0, 'text-red-600'],
              ['Today', stats.orders?.today ?? 0],
            ]}
          />
          <StatCard
            label="Food Sales"
            value={money(stats.revenue?.foodSales ?? stats.totalRevenue)}
            info="Total price of food items on DELIVERED orders only, before delivery fee, taxes and platform charges. Cancelled and unfinished orders are not counted."
            lines={[
              ['Commission', money(stats.revenue?.commission), 'text-amber-700'],
              ['Restaurant share', money(stats.revenue?.restaurantShare), 'text-green-700'],
              ['Customers paid', money(stats.revenue?.customerPaid)],
            ]}
          />
          <StatCard
            label="Menu Items"
            value={stats.items?.total ?? stats.totalItems ?? 0}
            info="Number of dishes on this restaurant's menu. 'Available' items can be ordered; 'Out of stock' items are hidden from ordering."
            lines={[
              ['Available', stats.items?.available ?? 0, 'text-green-700'],
              ['Out of stock', stats.items?.outOfStock ?? 0, 'text-red-600'],
            ]}
          />
          <StatCard
            label="Active Offers"
            value={stats.offers?.active ?? stats.totalOffers ?? 0}
            info="Offers that are switched on AND whose start/end dates include today. Paused or expired offers are not counted here."
            lines={[
              ['Total created', stats.offers?.total ?? 0],
              ['Paused / expired', stats.offers?.inactiveOrExpired ?? 0, 'text-gray-500'],
            ]}
          />
          <StatCard
            label="Customer Rating"
            value={`${Number(stats.rating?.average ?? restaurant.avgRating ?? 0).toFixed(1)} ⭐`}
            info="Average of all customer reviews for this restaurant (out of 5)."
            lines={[['Based on', `${stats.rating?.count ?? restaurant.totalRating ?? 0} review(s)`]]}
          />
        </div>
      </TooltipProvider>

      {/* Tabs */}
      <Tabs defaultValue="items" className="w-full">
        <div className="overflow-x-auto">
        <TabsList className="sm:grid sm:w-full sm:grid-cols-6">
          <TabsTrigger value="items">Menu Items</TabsTrigger>
          <TabsTrigger value="offers">Offers</TabsTrigger>
          <TabsTrigger value="gallery">Gallery</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="info">Info</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="items">
          <ItemsTab restaurantId={id} />
        </TabsContent>

        <TabsContent value="offers">
          <OffersTab restaurantId={id} />
        </TabsContent>

        <TabsContent value="gallery">
          <GalleryTab restaurantId={id} restaurant={restaurant} onUpdate={fetchRestaurant} />
        </TabsContent>

        <TabsContent value="orders">
          <OrderTab restaurantId={id} restaurant={restaurant}  />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab restaurantId={id} />
        </TabsContent>

        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Restaurant Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Owner Name</p>
                  <p className="font-medium">{restaurant.ownerName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{restaurant.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{restaurant.userEmail || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600"></p>
                  <p className="font-medium">{restaurant.gstNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">FSSAI Number</p>
                  <p className="font-medium">{restaurant.fssaiNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Commission</p>
                  <p className="font-medium">
                    {restaurant.commissionType === 'PERCENTAGE' 
                      ? `${restaurant.commissionValue}%` 
                      : `₹${restaurant.commissionValue}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pure Veg</p>
                  <p className="font-medium">
                    {restaurant.isPureVeg ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <span className="w-2 h-2 bg-green-600 rounded-full" />
                        Yes
                      </span>
                    ) : (
                      <span className="text-gray-700">No</span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RestaurantProfile;
